<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="invoices-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Faktury</h1>
      </div>
    </div>
    <v-row>
      <v-col cols="12">
        <v-tabs v-model="activeTab" class="mb-2">
          <v-tab>
            {{ $t('invoices.tabs.pending') }}
            <v-chip x-small class="ml-2" color="warning" text-color="white">{{ countsByTab.pending }}</v-chip>
          </v-tab>
          <v-tab>
            {{ $t('invoices.tabs.accepted') }}
            <v-chip x-small class="ml-2" color="success" text-color="white">{{ countsByTab.accepted }}</v-chip>
          </v-tab>
          <v-tab>
            {{ $t('invoices.tabs.company') }}
            <v-chip x-small class="ml-2" color="orange" text-color="white">{{ countsByTab.company }}</v-chip>
          </v-tab>
          <v-tab>
            {{ $t('invoices.tabs.rejected') }}
            <v-chip x-small class="ml-2" color="error" text-color="white">{{ countsByTab.rejected }}</v-chip>
          </v-tab>
        </v-tabs>
        <v-tabs
          v-if="activeTab === 0"
          v-model="pendingSubTab"
          class="mb-2"
          background-color="transparent"
          slider-color="primary"
        >
          <v-tab>
            Faktury
            <v-chip x-small class="ml-2" color="primary" text-color="white">{{ countsByTab.pendingInvoice }}</v-chip>
          </v-tab>
          <v-tab>
            Potwierdzenia
            <v-chip x-small class="ml-2" color="grey darken-1" text-color="white">{{ countsByTab.pendingConfirmation }}</v-chip>
          </v-tab>
        </v-tabs>

        <!-- Podsumowanie zwrotów za filtrowany okres -->
        <v-row dense class="px-3 pt-3">
          <v-col cols="6" md="3">
            <v-card outlined class="pa-3">
              <div class="text-caption grey--text">Oczekujące na akceptację</div>
              <div class="text-h6 font-weight-bold">{{ returnsSummary.pendingCount }}</div>
              <div class="text-caption">zadeklarowane: {{ fmtZl(returnsSummary.pendingTotal) }}</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="pa-3">
              <div class="text-caption grey--text">Przyznane zwroty (okres)</div>
              <div class="text-h6 font-weight-bold green--text text--darken-2">{{ fmtZl(returnsSummary.returnTotal) }}</div>
              <div class="text-caption">{{ returnsSummary.returnCount }} faktur</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="pa-3">
              <div class="text-caption grey--text">Do wypłaty (nierozliczone)</div>
              <div class="text-h6 font-weight-bold orange--text text--darken-2">{{ fmtZl(returnsSummary.unsettledTotal) }}</div>
              <div class="text-caption">{{ returnsSummary.unsettledCount }} faktur</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined class="pa-3">
              <div class="text-caption grey--text">Już rozliczone (w wypłatach)</div>
              <div class="text-h6 font-weight-bold">{{ fmtZl(returnsSummary.settledTotal) }}</div>
              <div class="text-caption">{{ returnsSummary.settledCount }} faktur</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Jedno odświeżenie danych z KSeF dla WSZYSTKICH faktur (limit 20 zapytań/h) -->
        <div class="d-flex align-center px-3 pb-2">
          <v-btn small outlined color="primary" :loading="ksefRefreshing" @click="refreshKsefData">
            <v-icon small left>mdi-cloud-download</v-icon>Odśwież dane z KSeF
          </v-btn>
          <span class="text-caption grey--text ml-3">
            Weryfikacja faktur działa na danych pobranych z KSeF (import co noc o 3:30). Odśwież, jeśli brakuje świeżych faktur.
          </span>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredInvoices"
          class="elevation-1"
          :loading="loading"
          :search="search"
          :items-per-page="25"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100], 'items-per-page-text': 'Na stronę:' }"
          :custom-filter="filterInvoices"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-file-document</v-icon></div>
              <div class="filter-title">{{ $t('invoices.title') }}</div>
              <v-spacer></v-spacer>
              <v-btn small outlined color="success" class="mr-2" @click="exportCsv" :loading="exportLoading">
                <v-icon left small>mdi-file-delimited</v-icon> {{ $t('invoices.csv') }}
              </v-btn>
              <v-btn small outlined color="info" class="mr-2" @click="downloadZip('INVOICE')" :loading="zipLoadingType === 'INVOICE'" :disabled="!!zipLoadingType">
                <v-icon left small>mdi-folder-zip</v-icon> Pobierz faktury
              </v-btn>
              <v-btn small outlined color="teal" class="mr-2" @click="downloadZip('CONFIRMATION')" :loading="zipLoadingType === 'CONFIRMATION'" :disabled="!!zipLoadingType">
                <v-icon left small>mdi-folder-zip</v-icon> Pobierz potwierdzenia transakcji
              </v-btn>
              <button class="action-pill action-pill--primary" @click="((dialog = true), (currentInvoiceId = null))">
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('invoices.addButton') }}
              </button>
            </div>
            <!-- AI-OCR: pasek batch + masowa akceptacja (tylko zakładka „Do akceptacji" i gdy OCR włączony) -->
            <div v-if="ocrEnabled && activeTab === 0" class="d-flex align-center flex-wrap px-4 py-2" style="gap:10px; background:rgba(84,120,255,.06); border-top:1px solid rgba(84,120,255,.12);">
              <v-btn small color="primary" :loading="ocrBatch.running" @click="runOcrBatch">
                <v-icon left small>mdi-text-recognition</v-icon>
                Sprawdź AI oczekujące
                <span v-if="ocrBatch.running"> — zostało {{ ocrBatch.remaining }}{{ ocrBatch.total ? '/' + ocrBatch.total : '' }}</span>
              </v-btn>
              <v-chip small color="green" dark><v-icon left x-small>mdi-check-circle</v-icon>{{ ocrSummary.zgodne }} zgodne</v-chip>
              <v-chip small color="amber darken-2" dark><v-icon left x-small>mdi-alert</v-icon>{{ ocrSummary.rozbieznosc }} rozbieżności</v-chip>
              <v-chip small color="blue-grey" dark><v-icon left x-small>mdi-help-circle</v-icon>{{ ocrSummary.niepewne }} niepewne</v-chip>
              <v-chip small outlined v-if="ocrSummary.niesprawdzone">{{ ocrSummary.niesprawdzone }} niesprawdzone</v-chip>
              <v-checkbox v-model="ocrFilterMismatch" label="Tylko rozbieżności" hide-details dense class="mt-0 pt-0" style="margin-top:0" />
              <v-spacer />
              <v-btn small color="success" :loading="ocrBatch.accepting" :disabled="!ocrEligible.length" @click="massAcceptOcr">
                <v-icon left small>mdi-check-all</v-icon>
                Akceptuj masowo zgodne ({{ ocrEligible.length }}) · zwrot {{ (ocrEligibleTotalReturn/100).toFixed(2) }} zł<template v-if="ocrEligibleCompany.length"> · {{ ocrEligibleCompany.length }} jako firmowe</template>
              </v-btn>
            </div>
            <div>
              <v-row class="mx-2">
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="search"
                    :label="$t('common.search')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-center">
                  <v-checkbox
                    v-model="tylkoDuble"
                    :label="'Tylko możliwe duble (' + liczbaDubli + ')'"
                    hide-details
                    dense
                    class="mt-0 pt-0"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-menu
                    ref="startDateMenu"
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="startDate"
                        :label="$t('common.dateFrom')"
                        outlined
                        readonly
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="clearStartDate"
                      />
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      no-title
                      scrollable
                      @input="startDateMenu = false"
                      first-day-of-week="1"
                      :locale="$i18n.locale"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <v-menu
                    ref="endDateMenu"
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="endDate"
                        :label="$t('common.dateTo')"
                        outlined
                        readonly
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="clearEndDate"
                      />
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      no-title
                      scrollable
                      @input="endDateMenu = false"
                      first-day-of-week="1"
                      :locale="$i18n.locale"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>
          </template>
          <template v-slot:item.ocr="{ item }">
            <v-tooltip bottom v-if="ocrEnabled && !item.accepted">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small :color="ocrStatusChip(rowOcrStatus(item)).color">
                  {{ ocrStatusChip(rowOcrStatus(item)).icon }}
                </v-icon>
              </template>
              <span>AI: {{ ocrStatusChip(rowOcrStatus(item)).label }}</span>
            </v-tooltip>
            <span v-else>—</span>
          </template>
          <template v-slot:item.dubel="{ item }">
            <v-tooltip v-if="item.duplikaty.length" bottom max-width="420">
              <template v-slot:activator="{ on }">
                <v-chip v-on="on" x-small dark :color="dubelChip(item.duplikaty[0].poziom).color">
                  <v-icon left x-small>{{ dubelChip(item.duplikaty[0].poziom).icon }}</v-icon>{{ dubelChip(item.duplikaty[0].poziom).label }}
                </v-chip>
              </template>
              <div v-for="(d, i) in item.duplikaty" :key="i" class="py-1">
                <b>{{ d.powod }}</b><br>
                {{ opisDubla(d) }}
              </div>
            </v-tooltip>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:item.driver="{ item }">
            <a href="#" @click.prevent="goToDriver(item.driverId)"
               style="text-decoration: underline; cursor: pointer; color: #1976d2;">
              {{ item.driver }}
            </a>
          </template>
          <template v-slot:item.dateSort="{ item }">{{ item.date }}</template>
          <template v-slot:item.category="{ item }">{{ getCategoryLabel(item.category) }}</template>
          <template v-slot:item.taxRate="{ item }">{{ item.taxRate ? item.taxRate + '%' : '—' }}</template>
          <template v-slot:item.bruttoNum="{ item }">{{ item.bruttoNum ? item.bruttoNum.toFixed(2) : '—' }}</template>
          <template v-slot:item.nettoNum="{ item }">{{ item.nettoNum ? item.nettoNum.toFixed(2) : '—' }}</template>
          <template v-slot:item.vatNum="{ item }">{{ item.vatNum ? item.vatNum.toFixed(2) : '—' }}</template>
          <template v-slot:item.driverCostNum="{ item }">{{ item.driverCostNum ? item.driverCostNum.toFixed(2) : '—' }}</template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="statusChipColor(item)"
              text-color="white"
            >
              {{ statusChipLabel(item) }}
            </v-chip>
          </template>
          <template v-slot:item.documentType="{ item }">
            <v-select
              :value="item.documentType || 'INVOICE'"
              :items="documentTypeOptions"
              dense
              hide-details
              outlined
              class="doc-type-select"
              style="max-width: 190px"
              @change="(val) => changeDocumentType(item, val)"
            ></v-select>
          </template>
          <template v-slot:item.details="{ item }">
            <v-btn
              small
              color="info"
              @click="viewInvoice(item.id)"
              class="mr-1"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="!item.accepted"
              small
              color="warning"
              @click="infoInvoice(item.id)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-chip
              v-if="item.accepted && !item.isCompanyInvoice"
              small
              color="success"
              text-color="white"
              class="ml-2"
            >
              <v-icon small left>mdi-check-circle</v-icon>
              {{ $t('invoices.statuses.accepted') }}
            </v-chip>
            <v-chip
              v-else-if="item.accepted && item.isCompanyInvoice"
              small
              color="orange"
              text-color="white"
              class="ml-2"
            >
              <v-icon small left>mdi-domain</v-icon>
              {{ $t('invoices.statuses.company') }}
            </v-chip>
          </template>
          <template v-slot:item.deleteInvoice="{ item }">
            <v-btn class="error" @click="deleteInvoice(item.id)"
              ><v-icon small class="pt-1">mdi-delete</v-icon></v-btn
            >
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <!-- ==================== CREATE DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('invoices.createTitle') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" @submit.prevent="submitInvoice">
            <v-row>
              <v-col cols="12">
                <v-file-input
                  v-model="invoice.file"
                  :label="$t('invoices.headers.file')"
                  outlined
                  dense
                  required
                  accept="image/*,.pdf"
                  prepend-icon=""
                  prepend-inner-icon="mdi-upload"
                  :rules="[validateRequired]"
                  @change="previewFile"
                />
                <iframe
                  v-if="invoice.preview"
                  :src="invoice.preview"
                  style="width: 100%; height: 400px"
                  frameborder="0"
                ></iframe>
              </v-col>
              <v-col cols="12">
                <v-menu
                  ref="dateMenu"
                  v-model="menuDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  content-class="dark-date-picker"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="invoice.date"
                      :label="$t('invoices.headers.date')"
                      outlined
                      dense
                      readonly
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      :rules="[validateRequired]"
                    />
                  </template>
                  <v-date-picker
                    v-model="invoice.date"
                    no-title
                    scrollable
                    @input="menuDate = false"
                    first-day-of-week="1"
                    :locale="$i18n.locale"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="invoice.name" :label="$t('invoices.headers.description')" outlined dense required type="text" :rules="[validateRequired]" />
              </v-col>
              <v-col cols="12"><v-divider class="mb-4" /><h3 class="invoice-section-title mb-3">{{ $t('invoices.vatBreakdown') }}</h3></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.brutto23" :label="$t('invoices.headers.brutto') + ' 23%'" outlined dense type="text" inputmode="decimal" :rules="[validateRequired]" @input="calculateNetto23" /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.netto23" :label="$t('invoices.headers.netto') + ' 23%'" outlined dense readonly /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.brutto8" :label="$t('invoices.headers.brutto') + ' 8%'" outlined dense type="text" inputmode="decimal" :rules="[validateRequired]" @input="calculateNetto8" /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.netto8" :label="$t('invoices.headers.netto') + ' 8%'" outlined dense readonly /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.brutto5" :label="$t('invoices.headers.brutto') + ' 5%'" outlined dense type="text" inputmode="decimal" :rules="[validateRequired]" @input="calculateNetto5" /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.netto5" :label="$t('invoices.headers.netto') + ' 5%'" outlined dense readonly /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.brutto0" :label="$t('invoices.headers.brutto') + ' 0%'" outlined dense type="text" inputmode="decimal" :rules="[validateRequired]" @input="calculateNetto0" /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.netto0" :label="$t('invoices.headers.netto') + ' 0%'" outlined dense readonly /></v-col>
              <v-col cols="6"><v-select v-model.number="invoice.returnToDriver" :items="returnToDriverOptions" :label="$t('invoices.driverReturn') + ' (%)'" outlined dense required :rules="[validateRequired]" @change="calculateDriverCost" /></v-col>
              <v-col cols="6"><v-text-field v-model="invoice.driverCost" :label="$t('invoices.driverAmount')" outlined dense readonly /></v-col>
              <v-col cols="12"><v-select v-model="invoice.type" :items="computedInvoiceType" :label="$t('invoices.headers.status')" outlined dense required :rules="[validateRequired]" /></v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="invoice.driver"
                  :items="drivers"
                  :label="$t('invoices.headers.driver')"
                  outlined dense required clearable
                  :rules="[validateRequired]"
                  item-text="name"
                  item-value="id"
                  :no-data-text="$t('common.noResults') || 'Brak wyników'"
                />
              </v-col>
              <v-col cols="12" v-if="!currentInvoiceId">
                <v-checkbox
                  v-model="invoice.skipFee"
                  hide-details
                  class="mt-0"
                  label="Nie naliczaj opłaty za dodanie tej faktury"
                />
                <div class="caption grey--text ml-8">
                  Domyślnie za dodanie faktury przez biuro naliczana jest opłata kierowcy. Zaznacz, aby jej nie naliczać.
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" text @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :disabled="!valid" @click="submitInvoice">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== ACCEPTANCE FULLSCREEN DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="acceptDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card class="accept-dialog-card" tile>
        <!-- Toolbar -->
        <v-toolbar dark color="#1A2433" flat dense class="accept-toolbar">
          <v-btn icon dark @click="closeAcceptDialog"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title class="text-body-1">
            {{ $t('invoices.acceptTitle') }} — {{ acceptDriverName }}
          </v-toolbar-title>
          <v-spacer />
          <div class="d-flex align-center">
            <v-btn text dark small :disabled="currentInvoiceIndex <= 0" @click="navigateInvoice(-1)" class="mr-1">
              <v-icon left small>mdi-chevron-left</v-icon> {{ $t('invoices.previousInvoice') }}
            </v-btn>
            <span class="text-caption mx-2" style="color: #90A4AE;">{{ currentInvoiceIndex + 1 }} / {{ acceptQueue.length }}</span>
            <v-btn text dark small :disabled="currentInvoiceIndex >= acceptQueue.length - 1" @click="navigateInvoice(1)">
              {{ $t('invoices.nextInvoice') }} <v-icon right small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-toolbar>

        <!-- Split View -->
        <div class="accept-split">
          <!-- LEFT: Preview -->
          <div class="accept-preview-panel">
            <div v-if="previewLoading" class="d-flex justify-center align-center" style="height: 100%">
              <v-progress-circular indeterminate color="primary" size="64" />
            </div>
            <iframe
              v-else-if="previewType === 'pdf' && previewSrc"
              :src="previewSrc"
              class="accept-preview-frame"
            />
            <div v-else-if="previewType === 'image' && previewSrc" class="accept-preview-image-wrap">
              <img :src="previewSrc" class="accept-preview-image" style="cursor: zoom-in;" @click="openImageZoom" title="Kliknij, aby powiększyć" />
            </div>
            <div v-else class="d-flex flex-column justify-center align-center" style="height: 100%; color: #546E7A;">
              <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
              <div class="mt-3">{{ $t('invoices.noFile') }}</div>
            </div>
            <!-- Download / zoom buttons -->
            <div v-if="previewSrc && !previewLoading" class="accept-preview-actions">
              <v-btn v-if="previewType === 'image'" small outlined dark class="mr-2" @click="openImageZoom">
                <v-icon left small>mdi-magnify-plus-outline</v-icon> Powiększ
              </v-btn>
              <v-btn small outlined dark @click="downloadCurrentFile">
                <v-icon left small>mdi-download</v-icon> {{ $t('common.download') }}
              </v-btn>
            </div>
          </div>

          <!-- RIGHT: Form -->
          <div class="accept-form-panel">
            <div class="accept-form-scroll">
              <v-alert
                v-if="acceptDuble.length"
                dense
                prominent
                :type="acceptDuble[0].poziom === 'KWOTA_TYDZIEN' ? 'info' : acceptDuble[0].poziom === 'DUBEL' ? 'error' : 'warning'"
                class="mb-3"
              >
                <div class="font-weight-bold mb-1">
                  {{ acceptDuble[0].poziom === 'DUBEL' ? 'Ta faktura już była' : 'Sprawdź, czy to nie dubel' }}
                </div>
                <div v-for="(d, i) in acceptDuble" :key="i" class="text-caption">
                  <b>{{ d.powod }}:</b> {{ opisDubla(d) }}
                </div>
              </v-alert>
              <!-- Typ dokumentu: faktura / potwierdzenie (zapis od razu) -->
              <div class="accept-section">
                <div class="accept-section-title">{{ $t('invoices.docTypeLabel') }}</div>
                <v-btn-toggle
                  v-model="invoice.documentType"
                  mandatory
                  dense
                  class="doc-type-toggle mb-1"
                  @change="saveDocumentType"
                >
                  <v-btn value="INVOICE" :loading="docTypeSaving && invoice.documentType === 'INVOICE'">
                    <v-icon left small>mdi-receipt-text-outline</v-icon>
                    {{ $t('invoices.docTypeInvoice') }}
                  </v-btn>
                  <v-btn value="CONFIRMATION" :loading="docTypeSaving && invoice.documentType === 'CONFIRMATION'">
                    <v-icon left small>mdi-file-check-outline</v-icon>
                    {{ $t('invoices.docTypeConfirmation') }}
                  </v-btn>
                </v-btn-toggle>
              </div>

              <!-- Weryfikacja w KSeF: czy taka faktura istnieje w systemie MF -->
              <div class="accept-section mt-2">
                <div class="d-flex align-center">
                  <v-progress-circular v-if="ksefChecking" indeterminate size="16" width="2" color="primary" class="mr-2" />
                  <v-chip small dark :color="invoice.ksefFound === true ? 'green' : invoice.ksefFound === false ? 'red' : 'grey'">
                    <v-icon left small>{{ invoice.ksefFound === true ? 'mdi-check-decagram' : invoice.ksefFound === false ? 'mdi-alert-circle' : 'mdi-help-circle' }}</v-icon>
                    {{ invoice.ksefFound === true ? 'Faktura jest w KSeF' : invoice.ksefFound === false ? 'NIE znaleziono w KSeF' : 'KSeF: niesprawdzona' }}
                  </v-chip>
                  <span v-if="invoice.ksefFound === null && !ksefChecking" class="text-caption grey--text ml-2">
                    brak w danych z KSeF — użyj „Odśwież dane z KSeF" nad tabelą
                  </span>
                </div>
                <div v-if="invoice.invoiceNumber" class="text-caption mt-1"><b>Nr faktury (od kierowcy):</b> {{ invoice.invoiceNumber }}</div>
                <div v-if="invoice.ksefNumber" class="text-caption mt-1 grey--text">Nr KSeF: {{ invoice.ksefNumber }}</div>

                <!-- Automatyczna weryfikacja danych z formularza kierowcy wg KSeF -->
                <div v-if="ksefCheck && ksefCheck.dateMatch !== null" class="mt-2 pa-2 rounded"
                  :style="ksefCheck.dateMatch === false || ksefCheck.amountMatch === false
                    ? 'background:#4a1f1f; border:1px solid #b23a3a;'
                    : 'background:#1f3a2a; border:1px solid #2e7d4f;'">
                  <div class="d-flex align-center mb-1">
                    <v-icon small :color="ksefCheck.dateMatch === false || ksefCheck.amountMatch === false ? 'red lighten-2' : 'green lighten-2'" class="mr-1">
                      {{ ksefCheck.dateMatch === false || ksefCheck.amountMatch === false ? 'mdi-alert' : 'mdi-check-circle' }}
                    </v-icon>
                    <b class="text-caption">
                      {{ ksefCheck.dateMatch === false || ksefCheck.amountMatch === false
                        ? 'Dane kierowcy NIE zgadzają się z fakturą w KSeF' : 'Data i kwota zgodne z KSeF' }}
                    </b>
                  </div>
                  <div class="text-caption" v-if="ksefCheck.dateMatch === false">
                    📅 Data — kierowca: <b>{{ fmtDay(ksefCheck.driverDate) }}</b>, w KSeF: <b>{{ fmtDay(ksefCheck.ksefDate) }}</b>
                  </div>
                  <div class="text-caption" v-if="ksefCheck.amountMatch === false">
                    💰 Kwota — kierowca: <b>{{ fmtZl(ksefCheck.driverBrutto / 100) }}</b>, w KSeF: <b>{{ fmtZl(ksefCheck.ksefBrutto / 100) }}</b>
                  </div>
                </div>

                <!-- OCR ze zdjęcia (gdy włączony) — dla faktur spoza KSeF -->
                <div v-if="ocrRunning" class="text-caption grey--text mt-2">
                  <v-progress-circular indeterminate size="12" width="2" class="mr-1" /> Odczytuję dane ze zdjęcia…
                </div>
                <div v-else-if="ocrCheck" class="mt-2 pa-2 rounded"
                  :style="ocrMismatch
                    ? 'background:#4a3a1f; border:1px solid #b28a3a;'
                    : 'background:#1f3a2a; border:1px solid #2e7d4f;'">
                  <div class="d-flex align-center mb-1">
                    <v-icon small :color="ocrMismatch ? 'amber lighten-2' : 'green lighten-2'" class="mr-1">
                      mdi-text-recognition
                    </v-icon>
                    <b class="text-caption">
                      {{ ocrMismatch ? 'AI: sprawdź — coś się nie zgadza' : 'AI: odczyt zgodny z danymi kierowcy' }}
                    </b>
                    <v-spacer />
                    <v-chip v-if="ocrCheck.typDokumentu" x-small label
                      :color="ocrCheck.typDokumentu === 'POTWIERDZENIE_TRANSAKCJI' ? 'amber darken-2' : 'blue-grey darken-1'" dark>
                      {{ docTypeLabel(ocrCheck.typDokumentu) }}
                    </v-chip>
                  </div>
                  <div class="text-caption" v-if="ocrCheck.ocrNumber">
                    #️⃣ Numer ze zdjęcia: <b>{{ ocrCheck.ocrNumber }}</b>
                    <span v-if="ocrCheck.numberMatch === false" class="amber--text text--lighten-2"> · kierowca: <b>{{ ocrCheck.driverNumber }}</b></span>
                  </div>
                  <div class="text-caption" v-if="ocrCheck.ocrDate">
                    📅 Data ze zdjęcia: <b>{{ fmtDay(ocrCheck.ocrDate) }}</b>
                    <span v-if="ocrCheck.dateMatch === false" class="amber--text text--lighten-2"> · kierowca: <b>{{ fmtDay(ocrCheck.driverDate) }}</b></span>
                  </div>
                  <div class="text-caption" v-if="ocrCheck.ocrBrutto">
                    💰 Kwota ze zdjęcia: <b>{{ fmtZl(ocrCheck.ocrBrutto / 100) }}</b>
                    <span v-if="ocrCheck.amountMatch === false" class="amber--text text--lighten-2"> · kierowca: <b>{{ fmtZl(ocrCheck.driverBrutto / 100) }}</b></span>
                  </div>
                  <div class="text-caption" v-if="ocrCheck.hasOurNip !== null"
                    :class="ocrCheck.hasOurNip ? 'green--text text--lighten-2' : 'amber--text text--lighten-2'">
                    {{ ocrCheck.hasOurNip ? '🧾 Nasz NIP na dokumencie — faktura uproszczona (VAT odliczalny)' : '⚠ Brak naszego NIP — nie odliczysz VAT' }}
                  </div>
                  <div class="text-caption grey--text" v-if="ocrCheck.ocrVendor">🏪 NIP sprzedawcy: {{ ocrCheck.ocrVendor }}</div>
                  <div class="text-caption amber--text text--lighten-2" v-if="ocrCheck.pewnosc === 'niska'">
                    ⚠ Niska pewność odczytu — sprawdź ręcznie.
                  </div>
                  <div class="text-caption grey--text" v-if="!ocrCheck.ocrDate && !ocrCheck.ocrBrutto && !ocrCheck.ocrNumber">
                    Nie udało się odczytać danych ze zdjęcia (słaba jakość?).
                  </div>
                </div>
              </div>

              <v-divider class="my-3" />

              <!-- Driver data (read-only) -->
              <div class="accept-section">
                <div class="accept-section-title">{{ $t('invoices.driverData') }}</div>
                <v-row dense>
                  <v-col cols="6">
                    <div class="accept-label">{{ $t('invoices.headers.description') }}</div>
                    <div class="accept-value">{{ invoice.name || '—' }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="accept-label">{{ $t('invoices.headers.date') }}</div>
                    <div class="accept-value">{{ invoice.date || '—' }}</div>
                  </v-col>
                  <v-col cols="6" v-if="invoice.category">
                    <div class="accept-label">{{ $t('invoices.headers.category') }}</div>
                    <div class="accept-value">
                      <v-chip small outlined>{{ getCategoryLabel(invoice.category) }}</v-chip>
                    </div>
                  </v-col>
                  <v-col cols="12" v-if="invoice.totalPrice && invoice.driverTaxRate">
                    <v-card outlined class="pa-3 mt-1" style="background: #212E3F; border-color: #2A3A50;">
                      <v-row dense align="center">
                        <v-col cols="3">
                          <div class="accept-label">{{ $t('invoices.headers.brutto') }}</div>
                          <div class="accept-value text-h6">{{ (invoice.totalPrice / 100).toFixed(2) }} zł</div>
                        </v-col>
                        <v-col cols="3">
                          <div class="accept-label">{{ $t('invoices.headers.netto') }}</div>
                          <div class="accept-value text-h6">{{ driverNetto }} zł</div>
                        </v-col>
                        <v-col cols="3">
                          <div class="accept-label">{{ $t('invoices.headers.vat') }}</div>
                          <div class="accept-value text-h6">{{ driverVatAmount }} zł</div>
                        </v-col>
                        <v-col cols="3">
                          <div class="accept-label">{{ $t('invoices.headers.taxRate') }}</div>
                          <v-chip small color="primary" text-color="white">{{ invoice.driverTaxRate }}%</v-chip>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                  <v-col cols="6" v-else>
                    <div class="accept-label">{{ $t('invoices.headers.driverCost') }}</div>
                    <div class="accept-value">{{ invoice.totalPrice != null ? (invoice.totalPrice / 100).toFixed(2) + ' zł' : '—' }}</div>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="my-3" />

              <!-- VAT breakdown -->
              <div class="accept-section">
                <div class="accept-section-title">{{ $t('invoices.vatBreakdown') }}</div>
                <v-form ref="acceptForm" v-model="acceptFormValid">
                  <v-row dense>
                    <v-col cols="6"><v-text-field v-model="invoice.brutto23" :label="$t('invoices.headers.brutto') + ' 23%'" outlined dense type="text" inputmode="decimal" @input="calculateNetto23" /></v-col>
                    <v-col cols="6"><v-text-field v-model="invoice.netto23" :label="$t('invoices.headers.netto') + ' 23%'" outlined dense readonly /></v-col>
                    <v-col cols="6"><v-text-field v-model="invoice.brutto8" :label="$t('invoices.headers.brutto') + ' 8%'" outlined dense type="text" inputmode="decimal" @input="calculateNetto8" /></v-col>
                    <v-col cols="6"><v-text-field v-model="invoice.netto8" :label="$t('invoices.headers.netto') + ' 8%'" outlined dense readonly /></v-col>
                    <v-col cols="6"><v-text-field v-model="invoice.brutto5" :label="$t('invoices.headers.brutto') + ' 5%'" outlined dense type="text" inputmode="decimal" @input="calculateNetto5" /></v-col>
                    <v-col cols="6"><v-text-field v-model="invoice.netto5" :label="$t('invoices.headers.netto') + ' 5%'" outlined dense readonly /></v-col>
                    <v-col cols="6"><v-text-field v-model="invoice.brutto0" :label="$t('invoices.headers.brutto') + ' 0%'" outlined dense type="text" inputmode="decimal" @input="calculateNetto0" /></v-col>
                    <v-col cols="6"><v-text-field v-model="invoice.netto0" :label="$t('invoices.headers.netto') + ' 0%'" outlined dense readonly /></v-col>
                  </v-row>

                  <v-divider class="my-3" />

                  <div class="accept-section-title">{{ $t('invoices.driverReturn') }}</div>
                  <v-row dense>
                    <v-col cols="6">
                      <v-select v-model.number="invoice.returnToDriver" :items="returnToDriverOptions" :label="$t('invoices.returnPercent')" outlined dense @change="calculateDriverCost" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="invoice.driverCost" :label="$t('invoices.driverAmount')" outlined dense readonly />
                    </v-col>
                  </v-row>
                </v-form>
              </div>
            </div>

            <!-- Actions (sticky bottom) -->
            <v-alert v-if="acceptTooOld && !invoice.accepted" dense text type="warning" class="mx-3 mb-0">
              Faktura wgrana później niż 7 dni od daty wystawienia — bez zwrotu dla kierowcy, akceptuj jako firmową.
            </v-alert>
            <div class="accept-actions">
              <v-btn color="success" large @click="acceptInvoice" :loading="acceptLoading" :disabled="acceptTooOld" class="mr-2">
                <v-icon left>mdi-check</v-icon> {{ $t('invoices.acceptBtn') }}
              </v-btn>
              <v-btn color="orange darken-1" large dark @click="acceptAsCompanyInvoice" :loading="acceptLoading" class="mr-2">
                <v-icon left>mdi-domain</v-icon> {{ $t('invoices.companyBtn') }}
              </v-btn>
              <v-btn color="error" large @click="rejectInvoice" :loading="acceptLoading">
                <v-icon left>mdi-close</v-icon> {{ $t('invoices.rejectBtn') }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- ==================== ZOOM ZDJĘCIA FAKTURY ==================== -->
    <v-dialog v-model="imageZoom.open" fullscreen hide-overlay transition="dialog-bottom-transition">
      <div class="img-zoom-overlay">
        <div class="img-zoom-toolbar">
          <v-btn icon dark @click="zoomOut" title="Pomniejsz"><v-icon>mdi-magnify-minus-outline</v-icon></v-btn>
          <span class="white--text mx-2" style="min-width: 56px; text-align: center;">{{ Math.round(imageZoom.scale * 100) }}%</span>
          <v-btn icon dark @click="zoomIn" title="Powiększ"><v-icon>mdi-magnify-plus-outline</v-icon></v-btn>
          <v-btn icon dark class="ml-2" @click="zoomReset" title="Dopasuj"><v-icon>mdi-fit-to-page-outline</v-icon></v-btn>
          <v-spacer />
          <v-btn small outlined dark class="mr-2" @click="downloadCurrentFile"><v-icon left small>mdi-download</v-icon> {{ $t('common.download') }}</v-btn>
          <v-btn icon dark @click="imageZoom.open = false" title="Zamknij"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="img-zoom-body">
          <img :src="previewSrc" class="img-zoom-img" :style="{ width: (imageZoom.scale * 100) + '%', cursor: imageZoom.scale >= 2 ? 'zoom-out' : 'zoom-in' }" @click="toggleZoom" />
        </div>
      </div>
    </v-dialog>

    <!-- ==================== VIEW FULLSCREEN DIALOG (read-only) ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="viewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card class="accept-dialog-card" tile>
        <v-toolbar dark color="#1A2433" flat dense class="accept-toolbar">
          <v-btn icon dark @click="closeViewDialog"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title class="text-body-1">
            {{ $t('invoices.viewTitle') }} — {{ viewDriverName }}
          </v-toolbar-title>
          <v-spacer />
          <div class="d-flex align-center">
            <v-btn text dark small :disabled="viewIndex <= 0" @click="navigateView(-1)" class="mr-1">
              <v-icon left small>mdi-chevron-left</v-icon> {{ $t('invoices.previousInvoice') }}
            </v-btn>
            <span class="text-caption mx-2" style="color: #90A4AE;">{{ viewIndex + 1 }} / {{ invoices.length }}</span>
            <v-btn text dark small :disabled="viewIndex >= invoices.length - 1" @click="navigateView(1)">
              {{ $t('invoices.nextInvoice') }} <v-icon right small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-toolbar>

        <div class="accept-split">
          <!-- LEFT: Preview -->
          <div class="accept-preview-panel">
            <div v-if="viewPreviewLoading" class="d-flex justify-center align-center" style="height: 100%">
              <v-progress-circular indeterminate color="primary" size="64" />
            </div>
            <iframe v-else-if="viewPreviewType === 'pdf' && viewPreviewSrc" :src="viewPreviewSrc" class="accept-preview-frame" />
            <div v-else-if="viewPreviewType === 'image' && viewPreviewSrc" class="accept-preview-image-wrap">
              <img :src="viewPreviewSrc" class="accept-preview-image" />
            </div>
            <div v-else class="d-flex flex-column justify-center align-center" style="height: 100%; color: #546E7A;">
              <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
              <div class="mt-3">{{ $t('invoices.noFile') }}</div>
            </div>
            <div v-if="viewPreviewSrc && !viewPreviewLoading" class="accept-preview-actions">
              <v-btn small outlined dark @click="downloadFile(viewData.file[0])">
                <v-icon left small>mdi-download</v-icon> {{ $t('common.download') }}
              </v-btn>
            </div>
          </div>

          <!-- RIGHT: Details (read-only) -->
          <div class="accept-form-panel">
            <div class="accept-form-scroll">
              <div class="accept-section">
                <div class="accept-section-title">{{ $t('invoices.driverData') }}</div>
                <v-row dense>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.headers.description') }}</div><div class="accept-value">{{ viewData.name || '—' }}</div></v-col>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.headers.date') }}</div><div class="accept-value">{{ viewData.date || '—' }}</div></v-col>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.headers.driver') }}</div><div class="accept-value">{{ viewDriverName }}</div></v-col>
                  <v-col cols="6">
                    <div class="accept-label">{{ $t('invoices.headers.status') }}</div>
                    <div class="accept-value">
                      <v-chip small :color="viewData.accepted ? (!viewData.isCompanyInvoice ? 'success' : 'orange') : 'warning'" text-color="white">
                        {{ viewData.accepted ? (!viewData.isCompanyInvoice ? $t('invoices.statuses.accepted') : $t('invoices.statuses.company')) : $t('invoices.statuses.pending') }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Driver amount breakdown -->
              <v-card v-if="viewData.totalPrice && viewData.taxRate" outlined class="pa-3 mt-2 mb-2" style="background: #212E3F; border-color: #2A3A50;">
                <div class="accept-section-title mb-2">{{ $t('invoices.headers.driverCost') }}</div>
                <v-row dense align="center">
                  <v-col cols="3"><div class="accept-label">{{ $t('invoices.headers.brutto') }}</div><div class="accept-value text-h6">{{ (viewData.totalPrice / 100).toFixed(2) }} zł</div></v-col>
                  <v-col cols="3"><div class="accept-label">{{ $t('invoices.headers.netto') }}</div><div class="accept-value text-h6">{{ viewNetto }} zł</div></v-col>
                  <v-col cols="3"><div class="accept-label">{{ $t('invoices.headers.vat') }}</div><div class="accept-value text-h6">{{ viewVat }} zł</div></v-col>
                  <v-col cols="3"><div class="accept-label">{{ $t('invoices.headers.taxRate') }}</div><v-chip small color="primary" text-color="white">{{ viewData.taxRate }}%</v-chip></v-col>
                </v-row>
              </v-card>

              <v-divider class="my-3" />

              <div class="accept-section">
                <div class="accept-section-title">{{ $t('invoices.vatBreakdown') }}</div>
                <v-row dense>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.headers.brutto') }} 23%</div><div class="accept-value">{{ viewData.brutto23 || '—' }}</div></v-col>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.headers.netto') }} 23%</div><div class="accept-value">{{ viewData.netto23 || '—' }}</div></v-col>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.headers.brutto') }} 8%</div><div class="accept-value">{{ viewData.brutto8 || '—' }}</div></v-col>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.headers.netto') }} 8%</div><div class="accept-value">{{ viewData.netto8 || '—' }}</div></v-col>
                </v-row>
              </div>

              <v-divider class="my-3" />

              <div class="accept-section">
                <div class="accept-section-title">{{ $t('invoices.driverReturn') }}</div>
                <v-row dense>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.returnPercent') }}</div><div class="accept-value">{{ viewData.driverReturnPercent != null ? viewData.driverReturnPercent + '%' : '—' }}</div></v-col>
                  <v-col cols="6"><div class="accept-label">{{ $t('invoices.driverAmount') }}</div><div class="accept-value">{{ viewData.driverReturn != null ? (viewData.driverReturn / 100).toFixed(2) + ' zł' : '—' }}</div></v-col>
                </v-row>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");
import * as dayjs from "dayjs";
export default {
  data() {
    return {
      currentInvoiceId: null,
      loading: false,
      totalItems: 0,
      search: "",
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,
      invoices: [],
      activeTab: 0,
      pendingSubTab: 0,
      menuDate: false,
      headers: [
        { text: "AI", align: "center", sortable: false, value: "ocr", width: 44 },
        { text: "Dubel", align: "center", sortable: false, value: "dubel", width: 60 },
        { text: "Data", align: "start", sortable: true, value: "dateSort" },
        { text: "Kategoria", align: "start", sortable: true, value: "category" },
        { text: "Stawka", align: "start", sortable: true, value: "taxRate" },
        { text: "Brutto", align: "start", sortable: true, value: "bruttoNum" },
        { text: "Netto", align: "start", sortable: true, value: "nettoNum" },
        { text: "Kwota VAT", align: "start", sortable: true, value: "vatNum" },
        { text: "Dla Kierowcy", align: "start", sortable: true, value: "driverCostNum" },
        { text: "Kierowca", align: "start", sortable: true, value: "driver" },
        { text: "Status", align: "start", sortable: true, value: "status" },
        { text: "Typ", align: "start", sortable: true, value: "documentType" },
        { text: "", value: "details", sortable: false },
        { text: "", value: "deleteInvoice", sortable: false },
      ],
      dialog: false,
      acceptDialog: false,
      acceptFormValid: true,
      acceptLoading: false,
      docTypeSaving: false,
      ksefChecking: false,
      ksefRefreshing: false,
      ksefCheck: null, // wynik porównania danych kierowcy z KSeF (data, kwota)
      ocrEnabled: false,
      ocrRunning: false,
      ocrCheck: null, // wynik odczytu ze zdjęcia (AI)
      ocrBatch: { running: false, accepting: false, remaining: 0, total: 0 },
      ocrFilterMismatch: false, // filtr „tylko rozbieżności"
      tylkoDuble: false, // filtr „tylko możliwe duble" (wszystkie zakładki)
      acceptDriverName: '',
      previewSrc: null,
      previewType: null,
      previewLoading: false,
      currentInvoiceIndex: -1,
      // Powiększanie zdjęcia faktury przy akceptacji.
      imageZoom: { open: false, scale: 1 },
      // View dialog
      viewDialog: false,
      viewDriverName: '',
      viewData: {},
      viewIndex: -1,
      viewPreviewSrc: null,
      viewPreviewType: null,
      viewPreviewLoading: false,
      exportLoading: false,
      zipLoading: false,
      zipLoadingType: null,
      valid: false,
      isEditing: false,
      invoice: {
        documentType: 'INVOICE',
        name: null,
        type: null,
        date: null,
        file: null,
        preview: null,
        brutto23: 0,
        brutto8: 0,
        brutto5: 0,
        brutto0: 0,
        netto23: 0,
        netto8: 0,
        netto5: 0,
        netto0: 0,
        returnToDriver: null,
        driverCost: null,
        accepted: false,
        skipFee: false,
        positions: [],
        // Pola ładowane dynamicznie w infoInvoice — MUSZĄ być zadeklarowane tu,
        // inaczej w Vue 2 są niereaktywne i driverNetto/driverVatAmount nie
        // przeliczają się przy przełączaniu faktur (zostają z poprzedniej).
        totalPrice: null,
        driverTaxRate: 0,
        driver: null,
        category: null,
      },
      drivers: [],
      returnToDriverOptions: [
        { text: "0%", value: 0 },
        { text: "25%", value: 25 },
        { text: "50%", value: 50 },
        { text: "75%", value: 75 },
        { text: "100%", value: 100 },
      ],
      invoiceType: [],
      documentTypeOptions: [
        { text: "Faktura", value: "INVOICE" },
        { text: "Potwierdzenie transakcji", value: "CONFIRMATION" },
      ],
    };
  },
  async created() {
    await this.getDriversSelect();
    await this.initialize();
  },
  computed: {
    ocrMismatch() {
      const o = this.ocrCheck;
      if (!o) return false;
      return o.dateMatch === false || o.amountMatch === false || o.numberMatch === false
        || o.hasOurNip === false || o.pewnosc === 'niska';
    },
    // Oczekujące faktury (zakładka „Do akceptacji") z załadowanej listy.
    pendingRows() {
      return this.invoices.filter((i) => !i.accepted && !i.deleted && i.file && i.file.length);
    },
    // Reguła 7 dni dla otwartej faktury w dialogu akceptacji.
    acceptTooOld() {
      return this.rowTooOld({
        ocrDate: this.invoice.ocrDate,
        dateSort: this.invoice.date,
        createdAtRaw: this.invoice.createdAtRaw,
      });
    },
    // Podsumowanie statusów AI w kolejce.
    ocrSummary() {
      const s = { zgodne: 0, rozbieznosc: 0, niepewne: 0, niesprawdzone: 0 };
      for (const r of this.pendingRows) {
        const st = this.rowOcrStatus(r);
        if (st === 'MATCH') s.zgodne++;
        else if (st === 'MISMATCH') s.rozbieznosc++;
        else if (st === 'UNSURE') s.niepewne++;
        else s.niesprawdzone++;
      }
      return s;
    },
    // Faktury kwalifikujące się do masowej akceptacji (zgodne + rozbicie VAT OK).
    ocrEligible() {
      return this.pendingRows.filter((r) => this.rowOcrStatus(r) === 'MATCH' && this.rowOcrReturn(r) !== null && !r.duplikaty.length);
    },
    // Zgodne z AI, ale wstrzymane jako możliwe duble — tylko ręcznie.
    ocrDubleWstrzymane() {
      return this.pendingRows.filter((r) => this.rowOcrStatus(r) === 'MATCH' && this.rowOcrReturn(r) !== null && r.duplikaty.length).length;
    },
    liczbaDubli() {
      return this.tabRows.filter((r) => r.duplikaty.length).length;
    },
    // Ostrzeżenia dla faktury otwartej w oknie akceptacji.
    acceptDuble() {
      const row = this.invoices.find((i) => i.id === this.currentInvoiceId);
      return row ? row.duplikaty : [];
    },
    // Reguła 7 dni: wgrane do tygodnia od wystawienia dostają zwrot,
    // starsze masowa akceptacja robi firmowymi (bez zwrotu).
    ocrEligibleReturn() {
      return this.ocrEligible.filter((r) => !this.rowTooOld(r));
    },
    ocrEligibleCompany() {
      return this.ocrEligible.filter((r) => this.rowTooOld(r));
    },
    ocrEligibleTotalReturn() {
      return this.ocrEligibleReturn.reduce((s, r) => s + (this.rowOcrReturn(r) || 0), 0);
    },
    returnsSummary() {
      // Podsumowanie „ile realnie zwracamy" za filtrowany okres (liczone z
      // załadowanej listy — strona i tak pobiera komplet faktur okresu).
      const act = this.invoices.filter((i) => !i.deleted && !i.isCompanyInvoice);
      const pending = act.filter((i) => !i.accepted);
      const withReturn = act.filter((i) => i.accepted && i.driverReturn > 0);
      const unsettled = withReturn.filter((i) => !i.settlementId);
      const settled = withReturn.filter((i) => i.settlementId);
      const sum = (arr, f) => arr.reduce((s, i) => s + f(i), 0);
      return {
        pendingCount: pending.length,
        pendingTotal: sum(pending, (i) => i.totalPriceNum || 0),
        returnCount: withReturn.length,
        returnTotal: sum(withReturn, (i) => i.driverReturn / 100),
        unsettledCount: unsettled.length,
        unsettledTotal: sum(unsettled, (i) => i.driverReturn / 100),
        settledCount: settled.length,
        settledTotal: sum(settled, (i) => i.driverReturn / 100),
      };
    },
    computedInvoiceType() {
      return [
        { text: this.$t('invoices.statuses.pending'), value: 'PENDING' },
        { text: this.$t('invoices.statuses.paid'), value: 'PAID' },
      ];
    },
    // Kolejka faktur DO AKCEPTACJI — paginacja w dialogu chodzi po tej liście,
    // nie po wszystkich fakturach (zaakceptowane/odrzucone nie wchodzą).
    acceptQueue() {
      return this.invoices.filter((inv) => !inv.accepted && !inv.deleted);
    },
    driverNetto() {
      if (!this.invoice.totalPrice || !this.invoice.driverTaxRate) return '—';
      const brutto = this.invoice.totalPrice / 100;
      const netto = brutto / (1 + this.invoice.driverTaxRate / 100);
      return netto.toFixed(2);
    },
    driverVatAmount() {
      if (!this.invoice.totalPrice || !this.invoice.driverTaxRate) return '—';
      const brutto = this.invoice.totalPrice / 100;
      const netto = brutto / (1 + this.invoice.driverTaxRate / 100);
      return (brutto - netto).toFixed(2);
    },
    viewNetto() {
      if (!this.viewData.totalPrice || !this.viewData.taxRate) return '—';
      const brutto = this.viewData.totalPrice / 100;
      return (brutto / (1 + this.viewData.taxRate / 100)).toFixed(2);
    },
    viewVat() {
      if (!this.viewData.totalPrice || !this.viewData.taxRate) return '—';
      const brutto = this.viewData.totalPrice / 100;
      const netto = brutto / (1 + this.viewData.taxRate / 100);
      return (brutto - netto).toFixed(2);
    },
    countsByTab() {
      const counts = {
        pending: 0,
        accepted: 0,
        company: 0,
        rejected: 0,
        pendingInvoice: 0,
        pendingConfirmation: 0,
      };
      for (const inv of this.invoices) {
        if (inv.deleted) counts.rejected++;
        else if (!inv.accepted) {
          counts.pending++;
          if (inv.documentType === 'CONFIRMATION') counts.pendingConfirmation++;
          else counts.pendingInvoice++;
        } else if (inv.isCompanyInvoice) counts.company++;
        else counts.accepted++;
      }
      return counts;
    },
    filteredInvoices() {
      return this.tylkoDuble ? this.tabRows.filter((r) => r.duplikaty.length) : this.tabRows;
    },
    tabRows() {
      switch (this.activeTab) {
        case 1:
          return this.invoices.filter((inv) => !inv.deleted && inv.accepted && !inv.isCompanyInvoice);
        case 2:
          return this.invoices.filter((inv) => !inv.deleted && inv.accepted && inv.isCompanyInvoice);
        case 3:
          return this.invoices.filter((inv) => inv.deleted);
        default: {
          const wantType = this.pendingSubTab === 1 ? 'CONFIRMATION' : 'INVOICE';
          let rows = this.invoices.filter(
            (inv) =>
              !inv.deleted &&
              !inv.accepted &&
              (inv.documentType || 'INVOICE') === wantType,
          );
          // Filtr „tylko rozbieżności" (znaczniki AI).
          if (this.ocrFilterMismatch) {
            rows = rows.filter((inv) => this.rowOcrStatus(inv) === 'MISMATCH');
          }
          return rows;
        }
      }
    },
  },
  watch: {
    startDate: {
      handler() {
        this.initialize();
      },
    },
    endDate: {
      handler() {
        this.initialize();
      },
    },
  },
  methods: {
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    goToDriver(id) {
      if (id) location.href = '/driver-details?id=' + id;
    },
    validateRequired(value) {
      return value !== null && value !== undefined && value !== "" || "To pole jest wymagane";
    },
    clearStartDate() {
      this.startDate = null;
    },
    clearEndDate() {
      this.endDate = null;
    },
    filterInvoices(value, search, item) {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (item.driver || '').toLowerCase().includes(s) ||
        (item.name || '').toLowerCase().includes(s) ||
        (item.date || '').toLowerCase().includes(s) ||
        this.getCategoryLabel(item.category).toLowerCase().includes(s) ||
        this.statusChipLabel(item).toLowerCase().includes(s)
      );
    },
    getCategoryLabel(cat) {
      const map = { FUEL: 'Paliwo', CAR_PARTS: 'Części samochodowe', SERVICE: 'Serwis / naprawa', OTHER: 'Inne' };
      return map[cat] || cat || '—';
    },
    statusChipColor(item) {
      if (item.deleted) return 'error';
      if (item.status === 'PAID') return 'success';
      if (item.status === 'PAYOUT_CREATED') return 'info';
      if (item.accepted) return item.isCompanyInvoice ? 'orange' : 'primary';
      return 'warning';
    },
    statusChipLabel(item) {
      if (item.deleted) return this.$t('invoices.statuses.rejected');
      if (item.status === 'PAID') return this.$t('invoices.statuses.paid');
      if (item.status === 'PAYOUT_CREATED') return this.$t('invoices.statuses.payoutCreated');
      if (item.accepted) {
        return item.isCompanyInvoice
          ? this.$t('invoices.statuses.company')
          : this.$t('invoices.statuses.accepted');
      }
      return this.$t('invoices.statuses.pending');
    },
    previewFile(fileObj) {
      if (fileObj && fileObj instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.invoice.preview = e.target.result;
        };
        reader.readAsDataURL(fileObj);
      }
    },
    async downloadFile(fileObj) {
      if (fileObj && fileObj.id) {
        try {
          const response = await fetch(`${process.env.VUE_APP_ROOT_API}aws/download-file/${fileObj.id}`, {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileObj.orginalName || fileObj.fileName || 'download';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          } else {
            throw new Error('Failed to download file');
          }
        } catch (error) {
          console.error('Download error:', error);
          Swal.fire(this.$t("common.error"), this.$t("invoices.downloadError"), "error");
        }
      }
    },
    // Parsuje kwotę wpisaną ręcznie — akceptuje kropkę I przecinek jako separator
    // dziesiętny (pola są type="text", bo type="number" przy polskiej lokalizacji
    // przeglądarki gubił kropkę: 149.10 → 14910).
    toNum(v) {
      if (v == null || v === '') return 0;
      const n = parseFloat(String(v).replace(',', '.').replace(/[^0-9.-]/g, ''));
      return isNaN(n) ? 0 : n;
    },
    calculateNetto0() {
        this.invoice.netto0 = (this.toNum(this.invoice.brutto0) / 1).toFixed(2);
        this.calculateDriverCost();
    },
    calculateNetto23() {
        this.invoice.netto23 = (this.toNum(this.invoice.brutto23) / 1.23).toFixed(2);
        this.calculateDriverCost();
    },
    calculateNetto8() {
        this.invoice.netto8 = (this.toNum(this.invoice.brutto8) / 1.08).toFixed(2);
        this.calculateDriverCost();
    },
    calculateNetto5() {
        this.invoice.netto5 = (this.toNum(this.invoice.brutto5) / 1.05).toFixed(2);
        this.calculateDriverCost();
    },
    calculateDriverCost() {
      if (this.invoice.returnToDriver != null) {
        // Calculate total tax from all VAT brackets
        const tax23 = this.toNum(this.invoice.brutto23) - this.toNum(this.invoice.netto23);
        const tax8 = this.toNum(this.invoice.brutto8) - this.toNum(this.invoice.netto8);
        const tax5 = this.toNum(this.invoice.brutto5) - this.toNum(this.invoice.netto5);
        const tax0 = this.toNum(this.invoice.brutto0) - this.toNum(this.invoice.netto0);

        const totalTax = tax23 + tax8 + tax5 + tax0;

        // Apply "zwrot dla kierowcy" percentage to total tax amount
        this.invoice.driverCost = (
          totalTax * (this.toNum(this.invoice.returnToDriver) / 100)
        ).toFixed(2);
      }
    },
    async submitInvoice() {
      // Process the invoice data here
      console.log("Invoice submitted:", this.invoice);
      await this.toggleLoading(true);
      if (!this.valid) return;
      // Combine predefined metadata with additional metadata
      const formData = new FormData();
      try {
        formData.append("file", this.invoice.file, this.invoice.file.name);
      } catch (e) {
        console.log("Missing Image");
      }
      formData.append("id", this.currentInvoiceId || undefined);
      formData.append("name", this.invoice.name);
      formData.append("brutto23", this.toNum(this.invoice.brutto23));
      formData.append("brutto8", this.toNum(this.invoice.brutto8));
      formData.append("brutto5", this.toNum(this.invoice.brutto5));
      formData.append("brutto0", this.toNum(this.invoice.brutto0));
      formData.append("netto23", this.toNum(this.invoice.netto23));
      formData.append("netto8", this.toNum(this.invoice.netto8));
      formData.append("netto5", this.toNum(this.invoice.netto5));
      formData.append("netto0", this.toNum(this.invoice.netto0));
      formData.append(
        "driverReturnPercent",
        this.toNum(this.invoice.returnToDriver)
      );
      formData.append("driverReturn", this.toNum(this.invoice.driverCost));
      formData.append("driverId", this.invoice.driver);
      formData.append("date", this.invoice.date);
      formData.append("status", this.invoice.type);
      formData.append("accepted", this.invoice.accepted);
      // Wyjątek: nie naliczaj opłaty za dodanie faktury przez biuro (tylko przy nowej).
      formData.append("skipFee", this.invoice.skipFee ? "true" : "false");
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "invoices/upload",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        await this.initialize();
        this.$root.$emit('invoices-updated');
        this.closeDialog();
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async initialize() {
      this.loading = true;
      this.fetchOcrEnabled();
      try {
        let url = process.env.VUE_APP_ROOT_API +
          `invoices/all?take=10000&skip=1`;

        if (this.startDate) {
          url += `&startDate=${this.startDate}`;
        }
        if (this.endDate) {
          url += `&endDate=${this.endDate}`;
        }

        const invoices = await axios({
          method: "get",
          url: url,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.invoices = invoices.data.data.result.map((bonus) => {
          const brutto = (bonus.brutto23 + bonus.brutto8 + bonus.brutto5 + bonus.brutto0) / 100;
          const netto = (bonus.netto23 + bonus.netto8 + bonus.netto5 + bonus.netto0) / 100;
          const vat = brutto - netto;
          return {
            brutto23: bonus.brutto23 / 100,
            brutto8: bonus.brutto8 / 100,
            brutto5: bonus.brutto5 / 100,
            brutto0: bonus.brutto0 / 100,
            netto23: bonus.netto23 / 100,
            netto8: bonus.netto8 / 100,
            netto5: bonus.netto5 / 100,
            netto0: bonus.netto0 / 100,
            returnToDriver: bonus.returnToDriver / 100,
            driverCost: bonus.driverReturn / 100,
            bruttoNum: brutto,
            nettoNum: netto,
            vatNum: vat,
            driverCostNum: bonus.driverReturn / 100,
            category: bonus.description || '',
            taxRate: bonus.taxRate || 0,
            driver: bonus.driver.firstName + " " + bonus.driver.lastName,
            driverId: bonus.driver?.id,
            status: bonus.status,
            accepted: bonus.accepted || false,
            deleted: bonus.deleted || false,
            rejectionReason: bonus.rejectionReason || null,
            driverReturn: bonus.driverReturn || 0,
            totalPriceNum: (bonus.totalPrice || 0) / 100,
            settlementId: bonus.settlementId || null,
            isCompanyInvoice: bonus.isCompanyInvoice || false,
            file: bonus.file || [],
            documentType: bonus.documentType || 'INVOICE',
            dateSort: bonus.date,
            date: dayjs(bonus.date).format("DD-MM-YYYY"),
            createdAtRaw: bonus.createdAt,
            id: bonus.id,
            // AI-OCR: surowe pola do znaczników w kolejce i masowej akceptacji.
            invoiceNumber: bonus.invoiceNumber || null,
            totalPrice: bonus.totalPrice || 0,
            ocrCheckedAt: bonus.ocrCheckedAt || null,
            ocrBrutto: bonus.ocrBrutto != null ? bonus.ocrBrutto : null,
            ocrNumber: bonus.ocrNumber || null,
            ocrDate: bonus.ocrDate || null,
            ocrError: bonus.ocrError || null,
            ocrData: bonus.ocrData || null,
            // Możliwe duble liczone na serwerze z pełnej historii kierowcy.
            duplikaty: bonus.duplikaty || [],
          };
        });
        this.totalItems = invoices.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response.data.message.toString(), "error");
      }
      this.loading = false;
    },
    async deleteInvoice(id) {
      Swal.fire({
        title: this.$t('invoices.confirmReject'),
        text: this.$t('invoices.confirmRejectText'),
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "delete",
              url: process.env.VUE_APP_ROOT_API + "invoices/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
            this.$root.$emit('invoices-updated');
          } catch (e) {
            console.log(e);
            Swal.fire(this.$t("common.error"), e.response.data.message.toString(), "error");
          }
        }
      });
    },
    async getDriversSelect() {
      await this.toggleLoading(true);
      try {
        const drivers = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `drivers?take=100000&skip=1`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.drivers = drivers.data.data.result.map((it) => {
          return {
            id: it.id,
            name: `${it.firstName} ${it.lastName}`,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async infoInvoice(id) {
      await this.toggleLoading(true);
      try {
        const invoices = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "invoices/details/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        const data = invoices.data.data;
        this.currentInvoiceId = data.id;
        this.isEditing = true;
        this.invoice.driver = data.driver.id;
        this.acceptDriverName = `${data.driver.firstName} ${data.driver.lastName}`;
        this.invoice.date = dayjs(data.date).format("YYYY-MM-DD");
        this.invoice.name = data.name;
        this.invoice.totalPrice = data.totalPrice;
        this.invoice.file = data.file || [];
        this.invoice.brutto23 = data.brutto23 / 100;
        this.invoice.brutto8 = data.brutto8 / 100;
        this.invoice.brutto5 = data.brutto5 / 100;
        this.invoice.brutto0 = data.brutto0 / 100;
        this.invoice.netto23 = data.netto23 / 100;
        this.invoice.netto8 = data.netto8 / 100;
        this.invoice.netto5 = data.netto5 / 100;
        this.invoice.netto0 = data.netto0 / 100;
        this.invoice.returnToDriver = data.driverReturnPercent;
        this.invoice.driverCost = data.driverReturn / 100;
        this.invoice.accepted = data.accepted || false;
        this.invoice.documentType = data.documentType || 'INVOICE';
        this.invoice.type = data.status;
        this.invoice.driverTaxRate = data.taxRate || 0;
        this.invoice.category = data.description || null;
        this.$set(this.invoice, 'invoiceNumber', data.invoiceNumber || null);
        this.$set(this.invoice, 'ksefFound', data.ksefFound !== undefined ? data.ksefFound : null);
        this.$set(this.invoice, 'ksefNumber', data.ksefNumber || null);
        this.$set(this.invoice, 'createdAtRaw', data.createdAt || null);
        this.$set(this.invoice, 'ocrDate', data.ocrDate || null);
        this.ksefCheck = null; // wynik porównania dotyczy konkretnej faktury
        this.ocrCheck = null;

        // Auto-fill brutto based on driver's selected VAT rate and totalPrice
        if (data.totalPrice && data.taxRate && !data.accepted) {
          const amount = data.totalPrice / 100;
          if (data.taxRate === 23) {
            this.invoice.brutto23 = amount;
            this.calculateNetto23();
          } else if (data.taxRate === 8) {
            this.invoice.brutto8 = amount;
            this.calculateNetto8();
          }
        }

        // Index w KOLEJCE do akceptacji (nie w pełnej liście) — dla paginacji.
        this.currentInvoiceIndex = this.acceptQueue.findIndex(i => i.id === id);

        // Open fullscreen acceptance dialog
        this.acceptDialog = true;
        await this.toggleLoading(false);

        // Auto-load file preview
        this.loadFilePreview();

        // Automatyczna weryfikacja w KSeF (dane lokalne — bez zużycia limitu)
        if (this.invoice.ksefFound === null && (this.invoice.invoiceNumber || this.invoice.name)) {
          await this.checkInvoiceKsef();
        }
        // OCR ze zdjęcia — tylko gdy włączony i gdy KSeF NIE potwierdził faktury
        // (dla potwierdzonych byłby zbędnym kosztem).
        if (this.ocrEnabled && this.invoice.ksefFound !== true) {
          this.runOcr();
        }
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response?.data?.message?.toString() || this.$t("common.error"), "error");
      }
    },
    fmtZl(v) {
      return (v || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł';
    },
    fmtDay(d) {
      return d ? dayjs(d).format('DD.MM.YYYY') : '—';
    },
    docTypeLabel(t) {
      return { FAKTURA: 'Faktura', PARAGON: 'Paragon', POTWIERDZENIE_TRANSAKCJI: 'Potwierdzenie', INNE: 'Inne' }[t] || t;
    },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },
    authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('token') }; },
    // Status zgodności AI dla wiersza kolejki (z surowych pól faktury).
    rowOcrStatus(r) {
      if (!r.ocrCheckedAt) return null; // niesprawdzona
      const ex = r.ocrData || {};
      if (r.ocrError || ex.pewnosc === 'niska') return 'UNSURE';
      if (r.ocrBrutto == null && !r.ocrNumber && !r.ocrDate) return 'UNSURE';
      const norm = (s) => String(s || '').replace(/[\s/\-.]/g, '').toUpperCase();
      const day = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : null);
      const amountBad = r.ocrBrutto != null && Math.abs(r.ocrBrutto - (r.totalPrice || 0)) > 1;
      const numberBad = r.invoiceNumber && r.ocrNumber && norm(r.invoiceNumber) !== norm(r.ocrNumber);
      const dateBad = r.ocrDate && day(r.ocrDate) !== day(r.dateSort);
      const nipBad = ex.hasOurNip === false;
      if (amountBad || numberBad || dateBad || nipBad) return 'MISMATCH';
      return 'MATCH';
    },
    // Zwrot 50% VAT z odczytu — null gdy rozbicie nie pokrywa kwoty (ręcznie).
    rowOcrReturn(r) {
      const ex = r.ocrData || {};
      const b23 = Math.round((ex.brutto23 || 0) * 100), n23 = Math.round((ex.netto23 || 0) * 100);
      const b8 = Math.round((ex.brutto8 || 0) * 100), n8 = Math.round((ex.netto8 || 0) * 100);
      if (Math.abs(b23 + b8 - (r.totalPrice || 0)) > 1) return null;
      const vat = (b23 - n23) + (b8 - n8);
      return Math.round(vat * 0.5);
    },
    // Reguła 7 dni: faktura wgrana później niż tydzień po wystawieniu nie
    // dostaje zwrotu. Data wystawienia = odczyt AI albo data od kierowcy.
    rowTooOld(r) {
      const issue = r.ocrDate || r.dateSort;
      if (!issue || !r.createdAtRaw) return false;
      return dayjs(r.createdAtRaw).diff(dayjs(issue), 'day', true) > 7;
    },
    dubelChip(poziom) {
      if (poziom === 'DUBEL') return { color: 'red darken-1', icon: 'mdi-content-duplicate', label: 'Dubel' };
      if (poziom === 'SPRAWDZ') return { color: 'amber darken-3', icon: 'mdi-alert', label: 'Sprawdź' };
      return { color: 'blue-grey', icon: 'mdi-calendar-week', label: 'Kwota' };
    },
    opisDubla(d) {
      const f = d.faktura;
      const stan = f.status === 'PAID' ? 'wypłacona'
        : f.status === 'PAYOUT_CREATED' ? 'w wypłacie'
        : f.firmowa ? 'firmowa'
        : f.zaakceptowana ? 'zaakceptowana, czeka na wypłatę' : 'czeka na akceptację';
      return `nr ${f.numer || '—'} · ${(f.kwota / 100).toFixed(2).replace('.', ',')} zł · wystawiona ${dayjs(f.data).format('DD.MM.YYYY')} · wgrana ${dayjs(f.wgrana).format('DD.MM.YYYY')} · ${stan}`;
    },
    // Przy „Dubel” i „Sprawdź” akceptacja wymaga świadomego potwierdzenia.
    async potwierdzMimoDubla() {
      const powazne = this.acceptDuble.filter((d) => d.poziom !== 'KWOTA_TYDZIEN');
      if (!powazne.length) return true;
      const lista = powazne.map((d) => `<li><b>${d.powod}</b><br>${this.opisDubla(d)}</li>`).join('');
      const r = await Swal.fire({
        title: 'Możliwy dubel faktury',
        html: `Ten kierowca ma już podobną fakturę:<ul style="text-align:left;margin-top:8px">${lista}</ul>Zaakceptować mimo to?`,
        icon: 'warning', showCancelButton: true,
        confirmButtonText: 'Tak, to inna faktura', cancelButtonText: 'Wróć',
        confirmButtonColor: '#c62828',
      });
      return r.isConfirmed;
    },
    ocrStatusChip(st) {
      return {
        MATCH: { color: 'green', icon: 'mdi-check-circle', label: 'zgodne' },
        MISMATCH: { color: 'amber darken-2', icon: 'mdi-alert', label: 'rozbieżność' },
        UNSURE: { color: 'blue-grey', icon: 'mdi-help-circle', label: 'niepewne' },
      }[st] || { color: 'grey', icon: 'mdi-minus-circle-outline', label: 'niesprawdzone' };
    },
    // Weryfikacja w tle: start + odpytywanie postępu (bez timeoutów bramki).
    async runOcrBatch() {
      this.ocrBatch.running = true;
      try {
        const start = await axios({
          method: 'post', url: this.apiUrl('invoices/ocr-verify-pending'),
          headers: this.authHeaders(),
        });
        const sd = start.data?.data || {};
        this.ocrBatch.total = sd.remaining || 0;
        this.ocrBatch.remaining = sd.remaining || 0;
        if (!sd.started && !sd.alreadyRunning) {
          this.ocrBatch.running = false;
          if ((sd.remaining || 0) === 0) Swal.fire('Gotowe', 'Wszystkie oczekujące faktury są już sprawdzone.', 'info');
          return;
        }
        // Polling postępu co 3 s.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 3000));
          const pr = await axios({ method: 'get', url: this.apiUrl('invoices/ocr-progress'), headers: this.authHeaders() });
          const pd = pr.data?.data || {};
          this.ocrBatch.remaining = pd.remaining || 0;
          // Odświeżaj listę co jakiś czas, żeby znaczniki dochodziły na żywo.
          await this.initialize();
          if (!pd.running && (pd.remaining || 0) === 0) break;
          if (!pd.running) break;
        }
        Swal.fire({ title: 'Sprawdzanie AI zakończone', text: `Zgodne: ${this.ocrSummary.zgodne} · rozbieżności: ${this.ocrSummary.rozbieznosc} · niepewne: ${this.ocrSummary.niepewne}`, icon: 'success' });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się sprawdzić faktur', 'error');
      } finally {
        this.ocrBatch.running = false; this.ocrBatch.remaining = 0; this.ocrBatch.total = 0;
      }
    },
    // Masowa akceptacja zgodnych — podgląd sumy, potem zapis.
    async massAcceptOcr() {
      const list = this.ocrEligible;
      if (!list.length) { Swal.fire('Brak', 'Nie ma zgodnych faktur do masowej akceptacji.', 'info'); return; }
      const total = (this.ocrEligibleTotalReturn / 100).toFixed(2);
      const oldCount = this.ocrEligibleCompany.length;
      const r = await Swal.fire({
        title: 'Masowa akceptacja',
        html: `Zaakceptować <b>${list.length}</b> zgodnych faktur?<br>`
          + `${this.ocrEligibleReturn.length} ze zwrotem dla kierowców (50% VAT): <b>${total} zł</b>.`
          + (oldCount ? `<br><b>${oldCount}</b> wgranych później niż 7 dni od wystawienia → <b>jako firmowe, bez zwrotu</b>.` : '')
          + (this.ocrDubleWstrzymane ? `<br><b>${this.ocrDubleWstrzymane}</b> zgodnych wstrzymanych jako <b>możliwe duble</b> — do ręcznej akceptacji.` : '')
          + `<br><span style="font-size:12px;opacity:.8">Rozbieżności i niepewne pomijamy — do ręcznej akceptacji.</span>`,
        icon: 'question', showCancelButton: true,
        confirmButtonText: 'Akceptuj wszystkie zgodne', cancelButtonText: 'Anuluj',
        confirmButtonColor: '#2e7d4f',
      });
      if (!r.isConfirmed) return;
      this.ocrBatch.accepting = true;
      try {
        const res = await axios({
          method: 'post', url: this.apiUrl('invoices/ocr-accept-batch'),
          headers: this.authHeaders(), data: { invoiceIds: list.map((i) => i.id) },
        });
        const d = res.data?.data || {};
        await this.initialize();
        this.$root.$emit('invoices-updated');
        Swal.fire({ title: 'Zaakceptowano', html: `Zaakceptowano <b>${d.accepted}</b> faktur · zwrot łącznie <b>${((d.totalReturn||0)/100).toFixed(2)} zł</b>.${d.acceptedCompany ? `<br>Dodatkowo <b>${d.acceptedCompany}</b> jako firmowe (starsze niż 7 dni, bez zwrotu).` : ''}${d.skipped?.length ? `<br>Pominięto ${d.skipped.length} (do ręcznej akceptacji).` : ''}`, icon: 'success' });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zaakceptować', 'error');
      } finally {
        this.ocrBatch.accepting = false;
      }
    },
    // Jednorazowe pobranie faktur z KSeF do lokalnej bazy — dla WSZYSTKICH
    // faktur naraz (zamiast zapytania per faktura; limit KSeF = 20/h).
    async refreshKsefData() {
      this.ksefRefreshing = true;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "accounting/ksef/import-now",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {},
        });
        const d = res.data.data;
        Swal.fire('Dane z KSeF odświeżone',
          `Pobrano faktury kosztowe: +${d.imported} (sprawdzono ${d.checked}). Weryfikacja faktur kierowców działa teraz na aktualnych danych.`,
          'success');
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || e.message, 'error');
      } finally {
        this.ksefRefreshing = false;
      }
    },
    async fetchOcrEnabled() {
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'accounting/ocr/settings', {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.ocrEnabled = !!res.data.data.enabled;
      } catch (e) { this.ocrEnabled = false; }
    },
    // Odczyt danych ze zdjęcia faktury (płatny, więc tylko gdy włączony w ustawieniach
    // i tylko gdy KSeF nie potwierdził faktury — wynik jest zapamiętywany w bazie).
    async runOcr() {
      if (!this.ocrEnabled || !this.currentInvoiceId) return;
      this.ocrRunning = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'accounting/ocr/invoice/' + this.currentInvoiceId,
          {},
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.ocrCheck = res.data.data;
      } catch (e) {
        console.log('OCR:', e.response?.data?.message || e.message);
      } finally {
        this.ocrRunning = false;
      }
    },
    // Weryfikacja faktury TYLKO z lokalnej bazy (local=1) — nie zużywa limitu KSeF.
    async checkInvoiceKsef() {
      if (!this.currentInvoiceId) return;
      this.ksefChecking = true;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "accounting/ksef/check-driver-invoice/" + this.currentInvoiceId,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const d = res.data.data;
        if (d.inconclusive) {
          // brak w lokalnych danych — status zostaje „niesprawdzona" (bez alertu)
          return;
        }
        this.ksefCheck = d;
        this.$set(this.invoice, 'ksefFound', d.found);
        this.$set(this.invoice, 'ksefNumber', d.ksefNumber);
        const row = this.invoices.find((i) => i.id === this.currentInvoiceId);
        if (row) { row.ksefFound = d.found; row.ksefNumber = d.ksefNumber; }
        if (!d.found && d.candidates && d.candidates.length) {
          Swal.fire('Nie znaleziono 1:1',
            `Brak faktury o tym numerze/kwocie, ale w KSeF jest ${d.candidates.length} faktur o tej samej kwocie w podobnym okresie — sprawdź ręcznie w zakładce Księgowość.`,
            'warning');
        }
      } catch (e) {
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response?.data?.message?.toString() || this.$t("common.error"), "error");
      } finally {
        this.ksefChecking = false;
      }
    },
    async saveDocumentType() {
      // Zapis od razu po przełączeniu — działa też bez akceptowania faktury.
      if (!this.currentInvoiceId) return;
      this.docTypeSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "invoices/document-type/" + this.currentInvoiceId,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { documentType: this.invoice.documentType },
        });
        const row = this.invoices.find((i) => i.id === this.currentInvoiceId);
        if (row) row.documentType = this.invoice.documentType;
      } catch (e) {
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response?.data?.message?.toString() || this.$t("common.error"), "error");
      } finally {
        this.docTypeSaving = false;
      }
    },
    async acceptInvoice() {
      if (!(await this.potwierdzMimoDubla())) return;
      this.acceptLoading = true;
      try {
        const invoiceData = {
          invoiceId: this.currentInvoiceId,
          brutto23: (this.toNum(this.invoice.brutto23) * 100).toString(),
          netto23: (this.toNum(this.invoice.netto23) * 100).toString(),
          brutto8: (this.toNum(this.invoice.brutto8) * 100).toString(),
          netto8: (this.toNum(this.invoice.netto8) * 100).toString(),
          brutto5: (this.toNum(this.invoice.brutto5) * 100).toString(),
          netto5: (this.toNum(this.invoice.netto5) * 100).toString(),
          brutto0: (this.toNum(this.invoice.brutto0) * 100).toString(),
          netto0: (this.toNum(this.invoice.netto0) * 100).toString(),
          driverReturn: (this.toNum(this.invoice.driverCost) * 100).toString(),
          driverReturnPercent: this.toNum(this.invoice.returnToDriver).toString(),
          isCompanyInvoice: false,
        };

        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "invoices/accept-driver",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
          data: invoiceData,
        });
      } catch (e) {
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response?.data?.message?.toString() || this.$t("common.error"), "error");
        this.acceptLoading = false;
        return;
      }

      try {
        await this.initialize();
        this.$root.$emit('invoices-updated');
        await this.goToNextOrClose("Faktura została zaakceptowana");
      } catch (e) {
        console.log('post-accept navigation error', e);
      } finally {
        this.acceptLoading = false;
      }
    },
    async acceptAsCompanyInvoice() {
      if (!(await this.potwierdzMimoDubla())) return;
      const result = await Swal.fire({
        title: this.$t('invoices.confirmCompany'),
        text: this.$t('invoices.confirmCompanyText'),
        icon: "warning",
        input: "textarea",
        inputLabel: "Powód odrzucenia (zobaczy go kierowca w aplikacji)",
        inputPlaceholder: "Np. faktura wgrana po terminie 7 dni, faktura nie dotyczy działalności…",
        inputAttributes: { maxlength: 500 },
        showCancelButton: true,
        confirmButtonColor: "#FF9800",
        cancelButtonColor: "#546E7A",
        confirmButtonText: "Tak, akceptuj jako firmową",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;

      this.acceptLoading = true;
      try {
        const invoiceData = {
          rejectionReason: (result.value || '').trim() || undefined,
          invoiceId: this.currentInvoiceId,
          brutto23: (this.toNum(this.invoice.brutto23) * 100).toString(),
          netto23: (this.toNum(this.invoice.netto23) * 100).toString(),
          brutto8: (this.toNum(this.invoice.brutto8) * 100).toString(),
          netto8: (this.toNum(this.invoice.netto8) * 100).toString(),
          brutto5: (this.toNum(this.invoice.brutto5) * 100).toString(),
          netto5: (this.toNum(this.invoice.netto5) * 100).toString(),
          brutto0: (this.toNum(this.invoice.brutto0) * 100).toString(),
          netto0: (this.toNum(this.invoice.netto0) * 100).toString(),
          driverReturn: "0",
          driverReturnPercent: "0",
          isCompanyInvoice: true,
        };

        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "invoices/accept-driver",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
          data: invoiceData,
        });
      } catch (e) {
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response?.data?.message?.toString() || this.$t("common.error"), "error");
        this.acceptLoading = false;
        return;
      }

      try {
        await this.initialize();
        this.$root.$emit('invoices-updated');
        await this.goToNextOrClose("Faktura zaakceptowana jako firmowa");
      } catch (e) {
        console.log('post-accept navigation error', e);
      } finally {
        this.acceptLoading = false;
      }
    },
    async rejectInvoice() {
      const result = await Swal.fire({
        title: this.$t('invoices.confirmReject'),
        text: this.$t('invoices.confirmRejectText'),
        icon: "question",
        input: "textarea",
        inputLabel: "Powód odrzucenia (opcjonalnie)",
        inputPlaceholder: "Wpisz powód odrzucenia...",
        inputAttributes: { maxlength: 500 },
        showCancelButton: true,
        confirmButtonColor: "#F44336",
        cancelButtonColor: "#546E7A",
        confirmButtonText: "Tak, odrzuć",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;
      const reason = (result.value || "").trim();

      this.acceptLoading = true;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "invoices/" + this.currentInvoiceId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: reason ? { reason } : undefined,
        });
      } catch (e) {
        console.log(e);
        Swal.fire(this.$t("common.error"), e.response?.data?.message?.toString() || this.$t("common.error"), "error");
        this.acceptLoading = false;
        return;
      }

      try {
        await this.initialize();
        this.$root.$emit('invoices-updated');
        await this.goToNextOrClose("Faktura została odrzucona");
      } catch (e) {
        console.log('post-reject navigation error', e);
      } finally {
        this.acceptLoading = false;
      }
    },
    async goToNextOrClose(message) {
      // Po akceptacji/odrzuceniu (initialize() już przeładowało listę) faktura
      // wypadła z kolejki. Ten sam index wskazuje teraz kolejną do akceptacji.
      const q = this.acceptQueue;
      if (q.length === 0) {
        this.closeAcceptDialog();
        Swal.fire(this.$t('common.success'), message + " " + this.$t('invoices.noMore'), "success");
        return;
      }
      const idx = Math.min(Math.max(this.currentInvoiceIndex, 0), q.length - 1);
      Swal.fire({ title: this.$t('common.success'), text: message, icon: "success", timer: 1200, showConfirmButton: false });
      await this.infoInvoice(q[idx].id);
    },
    async loadFilePreview() {
      this.previewSrc = null;
      this.previewType = null;
      if (!this.invoice.file || this.invoice.file.length === 0) return;

      const fileObj = this.invoice.file[0];
      if (!fileObj || !fileObj.id) return;

      this.previewLoading = true;
      try {
        const response = await fetch(`${process.env.VUE_APP_ROOT_API}aws/download-file/${fileObj.id}`, {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
        });
        if (!response.ok) throw new Error('Failed to load file');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const fileName = (fileObj.orginalName || fileObj.url || '').toLowerCase();

        if (blob.type === 'application/pdf' || fileName.endsWith('.pdf')) {
          this.previewType = 'pdf';
        } else {
          this.previewType = 'image';
        }
        this.previewSrc = url;
      } catch (error) {
        console.error('Preview load error:', error);
        this.previewType = null;
        this.previewSrc = null;
      } finally {
        this.previewLoading = false;
      }
    },
    async downloadCurrentFile() {
      if (!this.invoice.file || this.invoice.file.length === 0) return;
      await this.downloadFile(this.invoice.file[0]);
    },
    openImageZoom() {
      if (this.previewType !== 'image' || !this.previewSrc) return;
      this.imageZoom.scale = 1;
      this.imageZoom.open = true;
    },
    zoomIn() {
      this.imageZoom.scale = Math.min(this.imageZoom.scale + 0.5, 6);
    },
    zoomOut() {
      this.imageZoom.scale = Math.max(this.imageZoom.scale - 0.5, 0.5);
    },
    zoomReset() {
      this.imageZoom.scale = 1;
    },
    toggleZoom() {
      // Klik na zdjęciu: przełącz między dopasowaniem a 2.5x (szybki podgląd).
      this.imageZoom.scale = this.imageZoom.scale >= 2 ? 1 : 2.5;
    },
    async navigateInvoice(direction) {
      // Paginacja chodzi po kolejce do akceptacji (już przefiltrowanej), więc
      // nie ma potrzeby pomijać zaakceptowanych/odrzuconych.
      const q = this.acceptQueue;
      const newIndex = this.currentInvoiceIndex + direction;
      if (newIndex < 0 || newIndex >= q.length) return;
      await this.infoInvoice(q[newIndex].id);
    },
    closeAcceptDialog() {
      if (this.previewSrc) {
        window.URL.revokeObjectURL(this.previewSrc);
      }
      this.acceptDialog = false;
      this.isEditing = false;
      this.previewSrc = null;
      this.previewType = null;
      this.previewLoading = false;
      this.currentInvoiceIndex = -1;
      this.acceptDriverName = '';
      this.currentInvoiceId = undefined;
    },
    async viewInvoice(id) {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "invoices/details/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const data = res.data.data;
        this.viewData = {
          name: data.name,
          date: dayjs(data.date).format("YYYY-MM-DD"),
          totalPrice: data.totalPrice,
          taxRate: data.taxRate,
          brutto23: data.brutto23 ? (data.brutto23 / 100).toFixed(2) : 0,
          netto23: data.netto23 ? (data.netto23 / 100).toFixed(2) : 0,
          brutto8: data.brutto8 ? (data.brutto8 / 100).toFixed(2) : 0,
          netto8: data.netto8 ? (data.netto8 / 100).toFixed(2) : 0,
          brutto5: data.brutto5 ? (data.brutto5 / 100).toFixed(2) : 0,
          netto5: data.netto5 ? (data.netto5 / 100).toFixed(2) : 0,
          brutto0: data.brutto0 ? (data.brutto0 / 100).toFixed(2) : 0,
          netto0: data.netto0 ? (data.netto0 / 100).toFixed(2) : 0,
          driverReturn: data.driverReturn,
          driverReturnPercent: data.driverReturnPercent,
          accepted: data.accepted,
          isCompanyInvoice: data.isCompanyInvoice || false,
          file: data.file || [],
        };
        this.viewDriverName = data.driver
          ? `${data.driver.firstName || ''} ${data.driver.lastName || ''}`.trim()
          : '—';
        this.viewIndex = this.invoices.findIndex(i => i.id === id);
        this.viewDialog = true;
        this.loadViewPreview();
      } catch (e) {
        console.error(e);
        Swal.fire(this.$t("common.error"), this.$t("invoices.loadError"), "error");
      }
    },
    async loadViewPreview() {
      this.viewPreviewSrc = null;
      this.viewPreviewType = null;
      if (!this.viewData.file || this.viewData.file.length === 0) return;
      const fileObj = this.viewData.file[0];
      if (!fileObj || !fileObj.id) return;

      this.viewPreviewLoading = true;
      try {
        const response = await fetch(`${process.env.VUE_APP_ROOT_API}aws/download-file/${fileObj.id}`, {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
        });
        if (!response.ok) throw new Error('Failed');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const fileName = (fileObj.orginalName || fileObj.url || '').toLowerCase();
        this.viewPreviewType = (blob.type === 'application/pdf' || fileName.endsWith('.pdf')) ? 'pdf' : 'image';
        this.viewPreviewSrc = url;
      } catch (e) {
        console.error('View preview error:', e);
      } finally {
        this.viewPreviewLoading = false;
      }
    },
    async navigateView(direction) {
      const newIndex = this.viewIndex + direction;
      if (newIndex < 0 || newIndex >= this.invoices.length) return;
      await this.viewInvoice(this.invoices[newIndex].id);
    },
    activeDocumentTypeParam() {
      // Filtr typu dokumentu tylko w zakładce "Do akceptacji" (pod-zakładki).
      if (this.activeTab !== 0) return null;
      return this.pendingSubTab === 1 ? 'CONFIRMATION' : 'INVOICE';
    },
    async changeDocumentType(item, value) {
      if (!value || value === item.documentType) return;
      try {
        await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'invoices/document-type/' + item.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { documentType: value },
        });
        await this.initialize();
      } catch (e) {
        console.error(e);
        Swal.fire(
          this.$t('common.error'),
          e.response?.data?.message?.toString() || 'Nie udało się zmienić typu dokumentu',
          'error',
        );
      }
    },
    async exportCsv() {
      this.exportLoading = true;
      try {
        let url = process.env.VUE_APP_ROOT_API + 'invoices/export-csv?';
        if (this.startDate) url += `startDate=${this.startDate}&`;
        if (this.endDate) url += `endDate=${this.endDate}&`;
        const docType = this.activeDocumentTypeParam();
        if (docType) url += `documentType=${docType}&`;
        const response = await fetch(url, {
          headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
        });
        if (!response.ok) throw new Error('Failed');
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'faktury.csv';
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      } catch (e) {
        console.error(e);
        Swal.fire(this.$t("common.error"), this.$t("invoices.csvError"), "error");
      } finally {
        this.exportLoading = false;
      }
    },
    async downloadZip(explicitDocType) {
      // 'INVOICE' → tylko faktury, 'CONFIRMATION' → tylko potwierdzenia transakcji.
      const docType = (explicitDocType === 'INVOICE' || explicitDocType === 'CONFIRMATION')
        ? explicitDocType
        : this.activeDocumentTypeParam();
      this.zipLoadingType = docType || 'ALL';
      try {
        let url = process.env.VUE_APP_ROOT_API + 'invoices/download-zip?';
        if (this.startDate) url += `startDate=${this.startDate}&`;
        if (this.endDate) url += `endDate=${this.endDate}&`;
        if (docType) url += `documentType=${docType}&`;
        const response = await fetch(url, {
          headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
        });
        if (!response.ok) throw new Error('Failed');
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = docType === 'CONFIRMATION' ? 'potwierdzenia_transakcji.zip' : 'faktury.zip';
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      } catch (e) {
        console.error(e);
        Swal.fire(this.$t("common.error"), this.$t("invoices.zipError"), "error");
      } finally {
        this.zipLoadingType = null;
      }
    },
    closeViewDialog() {
      if (this.viewPreviewSrc) window.URL.revokeObjectURL(this.viewPreviewSrc);
      this.viewDialog = false;
      this.viewData = {};
      this.viewDriverName = '';
      this.viewIndex = -1;
      this.viewPreviewSrc = null;
      this.viewPreviewType = null;
    },
    closeDialog() {
      this.dialog = false;
      this.isEditing = false;
      this.currentInvoiceId = undefined;
      this.invoice = {
        name: null,
        type: null,
        date: null,
        file: null,
        preview: null,
        brutto23: 0,
        brutto8: 0,
        brutto5: 0,
        brutto0: 0,
        netto23: 0,
        netto8: 0,
        netto5: 0,
        netto0: 0,
        returnToDriver: null,
        driverCost: null,
        accepted: false,
        skipFee: false,
        totalPrice: null,
        driverTaxRate: 0,
        positions: [],
      };
      this.$refs.form?.resetValidation();
    },
  },
};
</script>

<style scoped>
.invoices-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-pill--primary { background: #5478FF; color: #FFFFFF; }
.action-pill--primary:hover { background: #3F62E8; }
.invoices-page >>> .v-tab {
  color: #94A0B8 !important;
}
.invoices-page >>> .v-tab--active {
  color: #5478FF !important;
}
.invoices-page >>> .v-tabs-slider {
  background-color: #5478FF !important;
}
.invoices-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.invoices-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.invoices-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.invoices-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.invoices-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.invoices-page >>> .v-text-field input,
.invoices-page >>> .v-text-field textarea,
.invoices-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.invoices-page >>> .v-label {
  color: #94A0B8 !important;
}
.invoice-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #5478FF;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0;
}
.accept-dialog-card {
  background-color: #0F1923 !important;
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.accept-toolbar {
  flex: 0 0 auto;
  border-bottom: 1px solid #2A3A50;
}
.accept-split {
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
}
.accept-preview-panel {
  flex: 0 0 60%;
  background-color: #111B26;
  position: relative;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #2A3A50;
}
.accept-preview-frame {
  width: 100%;
  flex: 1 1 auto;
  border: none;
  background: #fff;
}
.accept-preview-image-wrap {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  padding: 16px;
}
.accept-preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.accept-preview-actions {
  flex: 0 0 auto;
  padding: 8px 16px;
  background-color: #1A2433;
  border-top: 1px solid #2A3A50;
}
/* Pełnoekranowy zoom zdjęcia faktury */
.img-zoom-overlay {
  position: fixed;
  inset: 0;
  background: #0A0F16;
  display: flex;
  flex-direction: column;
}
.img-zoom-toolbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #1A2433;
  border-bottom: 1px solid #2A3A50;
}
.img-zoom-body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 16px;
  text-align: center;
}
.img-zoom-img {
  display: block;
  margin: 0 auto;
  height: auto;
  max-width: none;
  transition: width 0.12s ease;
}
.accept-form-panel {
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  background-color: #1A2433;
}
.accept-form-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px;
}
.accept-section {
  margin-bottom: 8px;
}
.accept-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #90A4AE;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}
.accept-label {
  font-size: 12px;
  color: #546E7A;
  margin-bottom: 2px;
}
.accept-value {
  font-size: 15px;
  color: #FFFFFF;
  font-weight: 500;
  margin-bottom: 8px;
}
.accept-actions {
  flex: 0 0 auto;
  padding: 16px 20px;
  background-color: #1A2433;
  border-top: 1px solid #2A3A50;
  display: flex;
  align-items: center;
}
.accept-form-panel .doc-type-toggle .v-btn {
  text-transform: none;
  letter-spacing: normal;
  background: #212E3F !important;
  color: #B9C4D6 !important;
  border-color: #2A3A50 !important;
}
.accept-form-panel .doc-type-toggle .v-btn--active {
  background: #2962FF !important;
  color: #fff !important;
}
</style>
